import React from "react";
import { Link } from "react-router-dom";

const COMPANY_NAME = "SSQUARE LOGIC";
const PRODUCT_NAME = "StayServe";

const sections = [
  {
    number: "01",
    title: "Overview",
    content: (
      <>
        <p>
          This Refund Policy explains the circumstances under which payments
          made for {PRODUCT_NAME}, operated by {COMPANY_NAME}, may be
          cancelled, refunded or otherwise adjusted.
        </p>

        <p>
          This policy applies to subscriptions, trial offers, credit purchases
          and other paid services made through supported payment methods.
        </p>
      </>
    ),
  },
  {
    number: "02",
    title: "Payment Methods",
    content: (
      <>
        <p>
          {PRODUCT_NAME} may accept payments through third-party payment
          gateways, including Razorpay, as well as supported direct UPI
          payment methods.
        </p>

        <p>
          The payment method used does not by itself change the eligibility
          rules described in this Refund Policy.
        </p>
      </>
    ),
  },
  {
    number: "03",
    title: "Trial and Introductory Plans",
    content: (
      <>
        <p>
          Where a paid trial or introductory offer is provided, the applicable
          price, duration and renewal terms will be displayed before the
          customer completes the purchase.
        </p>

        <p>
          If the customer chooses to continue with the service after the
          applicable trial period, the subscription may renew according to the
          plan terms shown at the time of purchase.
        </p>
      </>
    ),
  },
  {
    number: "04",
    title: "Subscription Cancellation",
    content: (
      <>
        <p>
          Customers may request cancellation of their subscription through the
          available account or support channels.
        </p>

        <p>
          Cancellation of a subscription generally prevents future renewals.
          It does not automatically create a refund for a payment that has
          already been successfully processed.
        </p>

        <p>
          Any refund for an already-paid subscription will be considered in
          accordance with this policy and the applicable plan terms.
        </p>
      </>
    ),
  },
  {
    number: "05",
    title: "Refund Eligibility",
    content: (
      <>
        <p>
          Refund requests may be considered in situations including:
        </p>

        <ul>
          <li>
            A duplicate payment was successfully charged for the same
            transaction.
          </li>
          <li>
            A payment was successfully received but the corresponding service
            or credits were not delivered due to a verified technical issue.
          </li>
          <li>
            A transaction was incorrectly processed due to a verified payment
            or system error.
          </li>
          <li>
            A refund is otherwise required under applicable law or an
            expressly applicable offer or agreement.
          </li>
        </ul>

        <p>
          Eligibility is determined after reviewing the relevant transaction,
          account information and payment records.
        </p>
      </>
    ),
  },
  {
    number: "06",
    title: "Non-Refundable Situations",
    content: (
      <>
        <p>
          Except where required by applicable law or expressly agreed
          otherwise, refunds may not be available for:
        </p>

        <ul>
          <li>
            Services or credits that have already been substantially used.
          </li>
          <li>
            A subscription period that has already started and has been used
            by the customer.
          </li>
          <li>
            Customer dissatisfaction where the service was delivered as
            described and no qualifying refund circumstance exists.
          </li>
          <li>
            Failure to cancel a subscription before its applicable renewal
            date.
          </li>
          <li>
            Losses caused by incorrect information supplied by the customer.
          </li>
        </ul>
      </>
    ),
  },
  {
    number: "07",
    title: "Duplicate or Failed Payments",
    content: (
      <>
        <p>
          If your bank account or payment method appears to have been charged
          more than once for the same purchase, please contact us with the
          relevant transaction details.
        </p>

        <p>
          If a payment appears as failed, pending or reversed with the payment
          provider but the amount has been temporarily debited from your bank
          account, the transaction may require confirmation from the payment
          provider or banking system before any further action is taken.
        </p>

        <p>
          Customers should avoid making repeated payments for the same pending
          transaction until the payment status has been reasonably confirmed,
          where practical.
        </p>
      </>
    ),
  },
  {
    number: "08",
    title: "Razorpay Payments",
    content: (
      <>
        <p>
          Payments processed through Razorpay may be subject to additional
          payment-processing, settlement, reversal or refund procedures
          applicable to the transaction.
        </p>

        <p>
          Where a refund is approved for a Razorpay transaction, the refund
          will generally be initiated through the applicable payment
          mechanism associated with the original transaction.
        </p>

        <p>
          The time taken for the refunded amount to appear in the customer's
          bank account or payment instrument may depend on the payment
          provider and banking system.
        </p>
      </>
    ),
  },
  {
    number: "09",
    title: "Direct UPI Payments",
    content: (
      <>
        <p>
          Where a customer pays directly through a supported UPI payment
          method, the customer should retain the relevant UPI transaction ID,
          date, amount and payment confirmation.
        </p>

        <p>
          Approved refunds for direct UPI transactions may be processed using
          the applicable payment method or another suitable method determined
          by {COMPANY_NAME}.
        </p>

        <p>
          A screenshot or bank statement showing a debit does not by itself
          establish that {COMPANY_NAME} successfully received the payment.
          Transaction verification may be required before a refund is
          approved.
        </p>
      </>
    ),
  },
  {
    number: "10",
    title: "Refund Request Process",
    content: (
      <>
        <p>
          To request a refund, contact {COMPANY_NAME} using the contact details
          provided on the website.
        </p>

        <p>Please provide, where available:</p>

        <ul>
          <li>Account or registered email address</li>
          <li>Transaction date</li>
          <li>Transaction amount</li>
          <li>Payment method</li>
          <li>Payment or transaction reference ID</li>
          <li>Reason for the refund request</li>
          <li>Relevant payment confirmation or receipt</li>
        </ul>

        <p>
          Providing complete transaction information helps us investigate and
          process the request more efficiently.
        </p>
      </>
    ),
  },
  {
    number: "11",
    title: "Refund Review",
    content: (
      <>
        <p>
          Refund requests may be reviewed against our transaction records,
          account activity, service usage and applicable payment-provider
          records.
        </p>

        <p>
          We may request additional information where necessary to verify the
          transaction or prevent fraudulent refund requests.
        </p>

        <p>
          Approval of a refund does not imply that every similar request will
          automatically qualify for a refund.
        </p>
      </>
    ),
  },
  {
    number: "12",
    title: "Refund Processing Time",
    content: (
      <>
        <p>
          Once a refund is approved, processing time may vary depending on the
          payment method, payment provider, bank and other financial
          intermediaries involved in the transaction.
        </p>

        <p>
          {COMPANY_NAME} cannot guarantee the exact date on which a refunded
          amount will appear in the customer's account after the refund has
          been initiated.
        </p>
      </>
    ),
  },
  {
    number: "13",
    title: "Fraudulent or Unauthorized Transactions",
    content: (
      <>
        <p>
          If you believe that a payment was made without your authorization,
          contact your bank or payment provider immediately and also notify
          {COMPANY_NAME}.
        </p>

        <p>
          We may investigate the transaction and may request appropriate
          information from the customer or payment provider.
        </p>
      </>
    ),
  },
  {
    number: "14",
    title: "Changes to This Refund Policy",
    content: (
      <p>
        {COMPANY_NAME} may update this Refund Policy from time to time to
        reflect changes to our services, payment methods, business practices
        or applicable requirements. The updated version will be published on
        this page.
      </p>
    ),
  },
  {
    number: "15",
    title: "Contact Us",
    content: (
      <>
        <p>
          If you have questions about a payment, cancellation or refund,
          please contact {COMPANY_NAME} through the contact details provided
          on our website.
        </p>

        <Link
          to="/#contact"
          className="inline-flex mt-4 rounded-xl bg-orange-500 px-5 py-3 text-sm font-semibold text-white transition hover:bg-orange-600"
        >
          Contact {COMPANY_NAME} →
        </Link>
      </>
    ),
  },
];

export default function RefundPolicy() {
  return (
    <main className="min-h-screen bg-stone-50 text-stone-900">
      {/* Header */}
      <section className="border-b border-stone-200 bg-stone-950 text-white">
        <div className="mx-auto max-w-6xl px-6 py-20 lg:px-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-orange-400 transition hover:text-orange-300"
          >
            ← Back to {PRODUCT_NAME}
          </Link>

          <div className="mt-10 max-w-4xl">
            <div className="mb-5 inline-flex rounded-full border border-orange-500/30 bg-orange-500/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.18em] text-orange-400">
              Legal
            </div>

            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
              Refund Policy
            </h1>

            <p className="mt-6 max-w-3xl text-lg leading-8 text-stone-300">
              Our refund and cancellation guidelines for subscriptions,
              payments, trials and other paid services provided through{" "}
              {PRODUCT_NAME}.
            </p>

            <p className="mt-6 text-sm text-stone-500">
              Effective Date: 18 August 2026
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
        <div className="rounded-3xl border border-stone-200 bg-white p-6 shadow-sm sm:p-10">
          <div className="space-y-12">
            {sections.map((section) => (
              <section
                key={section.number}
                className="border-b border-stone-200 pb-10 last:border-b-0 last:pb-0"
              >
                <div className="flex gap-5">
                  <div className="hidden shrink-0 text-sm font-bold text-orange-500 sm:block">
                    {section.number}
                  </div>

                  <div className="min-w-0 flex-1">
                    <h2 className="text-2xl font-bold tracking-tight text-stone-950">
                      {section.title}
                    </h2>

                    <div className="mt-5 space-y-4 text-[15px] leading-7 text-stone-600">
                      {section.content}
                    </div>
                  </div>
                </div>
              </section>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-6 pb-16 lg:px-8">
        <div className="rounded-3xl bg-stone-950 px-8 py-12 text-center text-white">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-orange-400">
            {COMPANY_NAME}
          </p>

          <h2 className="mt-3 text-3xl font-bold">
            Need help with a payment?
          </h2>

          <p className="mx-auto mt-4 max-w-2xl text-stone-400">
            Contact our team with your transaction details and we will review
            the request.
          </p>

          <Link
            to="/#contact"
            className="mt-7 inline-flex rounded-xl bg-orange-500 px-6 py-3 font-semibold text-white transition hover:bg-orange-600"
          >
            Contact Us →
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-8 text-sm text-stone-500 sm:flex-row sm:items-center sm:justify-between lg:px-8">
          <p>
            © {new Date().getFullYear()} {COMPANY_NAME}. All rights reserved.
          </p>

          <div className="flex flex-wrap gap-5">
            <Link
              to="/privacy-policy"
              className="transition hover:text-orange-500"
            >
              Privacy Policy
            </Link>

            <Link
              to="/terms-and-conditions"
              className="transition hover:text-orange-500"
            >
              Terms & Conditions
            </Link>

            <Link
              to="/refund-policy"
              className="text-orange-500"
            >
              Refund Policy
            </Link>

            <Link
              to="/cookie-policy"
              className="transition hover:text-orange-500"
            >
              Cookie Policy
            </Link>
          </div>
        </div>
      </footer>
    </main>
  );
}